// UC25 — the Agregări register (Administrator Sistem).
// per wireframes/react/src/pages/AdminAgregari.tsx (lista + dialogurile)
// per docs/caiet-uc-spec.md §UC25-01 §UC-AGG-07 §UC-AGG-08 §UC-AGG-10
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import type { TableColumn, TableRowData } from '@egov-moldova/mud/loader';
import {
  MudButton,
  MudIcon,
  MudSelect,
  MudSpinner,
  MudTable,
  MudTag,
  MudTooltip,
} from '@egov-moldova/mud-react';
import {
  type ActiveFilter,
  EmptyRegisterMessage,
  FilterBar,
  formatDateTime,
  onRowOpen,
  PageHeader,
  RouterLink,
  RowActionsMenu,
} from '@simsm/ui-shared';
import { QueryState } from '../../components/QueryState/QueryState';
import { listAggregates, type AggregateStatus } from './aggregates-domain';
import { useAggregateActions } from './useAggregateActions';
import './aggregates.css';

type StatusFilter = '' | 'active' | 'archived';

function normalize(value: string): string {
  return value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

export function statusSemantic(
  status: AggregateStatus,
): 'success' | 'brand' | 'danger' | 'neutral' {
  if (status === 'active') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'updating' || status === 'draft') return 'brand';
  return 'neutral';
}

export function AggregatesPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const search = params.get('q') ?? '';
  const statusFilter = (params.get('status') ?? '') as StatusFilter;

  function setFilter(key: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  const aggregates = useQuery({ queryKey: ['admin-aggregates'], queryFn: listAggregates });

  // The register's row menu and the fișa's header menu offer the same acts, so
  // they share one hook rather than two copies of six confirmations
  // (wireframe AdminAgregari.tsx:655, reused at :1496).
  const { actions, dialogs, busyName } = useAggregateActions({
    onOpen: (aggregate) => navigate(`/system-admin/aggregates/${aggregate.name}`),
    onEdit: (aggregate) => navigate(`/system-admin/aggregates/${aggregate.name}/edit`),
  });

  const all = aggregates.data ?? [];
  const rows = useMemo(() => {
    const needle = normalize(search.trim());
    return all
      .filter((aggregate) => {
        if (statusFilter === 'active' && aggregate.status === 'archived') return false;
        if (statusFilter === 'archived' && aggregate.status !== 'archived') return false;
        if (!needle) return true;
        const haystack = normalize(
          [
            aggregate.displayName,
            aggregate.name,
            aggregate.description ?? '',
            ...aggregate.groupBy.map((dimension) => t(`aggregates.dimension.${dimension}`)),
          ].join(' '),
        );
        return haystack.includes(needle);
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName, 'ro'));
  }, [all, search, statusFilter, t]);

  const activeCount = all.filter((aggregate) => aggregate.status !== 'archived').length;

  // The search term gets no chip: it clears from the box's own ×, which is the
  // wireframe's own rule for the shared bar (BaraFiltre.tsx:35).
  const activeFilters: ActiveFilter[] = statusFilter
    ? [
        {
          key: 'status',
          label: t('aggregates.column.status'),
          value: t(
            statusFilter === 'active' ? 'aggregates.filter.active' : 'aggregates.filter.inactive',
          ),
          onRemove: () => setFilter('status', ''),
        },
      ]
    : [];

  // The wireframe's `tbl zebra compact … leading-actions-table`
  // (AdminAgregari.tsx:1882-1888) as AGE's own table. The leading column is the
  // shared row-actions track (docs/ui-shape.md §Shared leading row-actions
  // column): a 44px content track plus mud-table's cell padding on each side,
  // no visible heading, the accessible name slotted in. Every cell is slotted,
  // so mud-table's nowrap `.cell-text` never ellipsises a name.
  //
  // 76 = 44 + 16 + 16. It was 92, which is 44 plus AGE's UNOVERRIDDEN 24px
  // padding — but `.simsm-table` re-points that custom property to 16
  // (global.css §.simsm-table), so the track carried 16px it had no content
  // for and the register's first column sat wider than the wireframe's.
  const columns: TableColumn[] = [
    { key: 'actions', label: '', width: '76px' },
    { key: 'name', label: t('aggregates.column.name') },
    // UC-AGG-01 / BUG-1-027: day, month or year (or the week an administrator made).
    { key: 'interval', label: t('aggregates.column.interval'), width: '110px' },
    { key: 'lastRefresh', label: t('aggregates.column.last_refresh'), width: '22%' },
    { key: 'status', label: t('aggregates.column.status'), width: '130px' },
  ];
  const tableRows: TableRowData[] = rows.map((aggregate) => ({ id: aggregate.name }));

  // An empty register keeps its table. The wireframe leaves the head, the
  // frame and the footer standing and puts the message in one centred cell
  // spanning every column (AdminAgregari.tsx:1926); a box in
  // place of the register hides what the register would even hold. Both
  // QueryState branches render this same node, because mud-table draws its
  // `empty` slot on its own once `rows` is [].
  const register = (
    <div className="simsm-aggregate__table">
      <MudTable
        className="simsm-table"
        columns={columns}
        rows={tableRows}
        rowStyle="zebra"
        disableSort={true}
        aria-label={t('aggregates.title')}
        // The whole row opens the fișa, the menu's first entry.
        hoverable
        onMudRowClick={onRowOpen((index) => {
          const aggregate = rows[index];
          if (aggregate) navigate(`/system-admin/aggregates/${aggregate.name}`);
        })}
      >
        <EmptyRegisterMessage>
          {all.length === 0 ? t('aggregates.empty') : t('aggregates.empty_filtered')}
        </EmptyRegisterMessage>
        <span slot="header-cell-actions" className="visually-hidden">
          {t('aggregates.column.actions')}
        </span>
        {rows.map((aggregate, index) => {
          const cell =
            aggregate.status === 'archived'
              ? 'simsm-aggregate__cell simsm-aggregate__cell--muted'
              : 'simsm-aggregate__cell';
          return [
            <span
              key={`actions-${aggregate.name}`}
              slot={`cell-actions-${index}`}
              className="simsm-aggregate__cell simsm-aggregate__cell--actions"
            >
              <RowActionsMenu
                label={t('aggregates.action.row_menu', { name: aggregate.displayName })}
                actions={actions(aggregate)}
              />
            </span>,
            // The name alone, as the wireframe draws it
            // (AdminAgregari.tsx:1896-1900). The description was being repeated
            // under every row: it is a paragraph, it doubled the height of a
            // register whose other three columns are one line each, and the fișa
            // is where it is read. It stays in the search haystack above, which
            // is what it was actually useful for here.
            <span key={`name-${aggregate.name}`} slot={`cell-name-${index}`} className={cell}>
              <RouterLink
                to={`/system-admin/aggregates/${aggregate.name}`}
                size="sm"
                variant="strict"
                underline="hover"
              >
                {aggregate.displayName || aggregate.name}
              </RouterLink>
            </span>,
            <span
              key={`interval-${aggregate.name}`}
              slot={`cell-interval-${index}`}
              className={cell}
            >
              {t(`aggregates.bucket.${aggregate.bucketInterval}`)}
            </span>,
            <span
              key={`refresh-${aggregate.name}`}
              slot={`cell-lastRefresh-${index}`}
              className={cell}
            >
              {busyName === aggregate.name ? (
                <span className="simsm-aggregate__busy" role="status">
                  <MudSpinner size="sm" label={t('common.loading')} />
                  {t('aggregates.state.running')}
                </span>
              ) : aggregate.lastRefreshAt ? (
                formatDateTime(aggregate.lastRefreshAt)
              ) : (
                <span className="simsm-aggregate__muted">{t('aggregates.state.never')}</span>
              )}
            </span>,
            <span key={`status-${aggregate.name}`} slot={`cell-status-${index}`} className={cell}>
              <MudTag
                variant="status"
                type={aggregate.status === 'active' ? 'outlined' : 'subtle'}
                semantic={statusSemantic(aggregate.status)}
                label={t(`aggregates.status.${aggregate.status}`)}
              />
            </span>,
          ];
        })}
      </MudTable>
    </div>
  );

  return (
    <section className="simsm-page simsm-aggregate">
      <PageHeader
        title={t('aggregates.title')}
        /* The wireframe's own header: one short sentence, with the caveat about
           corrections hanging off it as an info tip (AdminAgregari.tsx:1825-1830).
           Inlining the tip into the sentence wrapped the lede onto a second line
           and moved the filter bar and the whole register down 20px. */
        lede={
          <>
            {t('aggregates.lede')}
            <MudTooltip content={t('aggregates.lede_tip')} position="top">
              {/* `slot="trigger"` is required: unslotted, the icon lands inside
                  the bubble, the trigger measures 0x0 and the tip is
                  unhoverable (see SectionTip in MedicineFormPage.tsx). */}
              <MudIcon slot="trigger" name="circle-info" size={16} />
            </MudTooltip>
          </>
        }
        actions={
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            onClick={() => navigate('/system-admin/aggregates/new')}
          >
            <MudIcon slot="icon-start" name="plus-large" size={16} />
            {t('aggregates.action.create')}
          </MudButton>
        }
      />

      <FilterBar
        search={{
          value: search,
          onChange: (value) => setFilter('q', value),
          placeholder: t('aggregates.search.placeholder'),
          ariaLabel: t('aggregates.search.label'),
          clearLabel: t('common.filter.clear_search'),
        }}
        active={activeFilters}
        onReset={() => setParams(new URLSearchParams(), { replace: true })}
        labels={{
          filters: t('common.filter.button'),
          reset: t('common.filter.reset'),
          panelAriaLabel: t('aggregates.filter.aria'),
          activeAriaLabel: t('common.filter.active_aria_label'),
        }}
        info={
          <p className="simsm-aggregate__count">
            {t('aggregates.count', { active: activeCount, total: all.length })}
          </p>
        }
        panel={
          <label className="simsm-filter-bar__field">
            <span className="simsm-filter-bar__field-label">{t('aggregates.column.status')}</span>
            <MudSelect
              size="medium"
              value={statusFilter}
              aria-label={t('aggregates.column.status')}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                setFilter('status', e.detail.value)
              }
            >
              <option value="">{t('aggregates.filter.all')}</option>
              <option value="active">{t('aggregates.filter.active')}</option>
              <option value="archived">{t('aggregates.filter.inactive')}</option>
            </MudSelect>
          </label>
        }
      />

      <QueryState query={aggregates} isEmpty={rows.length === 0} empty={register}>
        {register}
      </QueryState>

      {dialogs}
    </section>
  );
}
